import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Reveal, Overline } from "@/components/Reveal";
import { SensorCone } from "@/components/Vectors";
import { SOLUTIONS } from "@/data/content";
import { SOLUTION_ICONS } from "@/lib/icons";
import { track } from "@/lib/api";

export default function Solutions() {
  return (
    <div className="bg-bone" data-testid="solutions-page">
      <SEO
        title="Solutions — Energy, Automation & Remote Management"
        description="Amaya Pro solutions: energy optimization, smart lighting automation, remote management, large facility control and building performance."
        path="/solutions"
      />
      <PageHero
        overline="Solutions"
        title="Lighting strategies, not just lighting products."
        sub="Five ways Amaya Pro turns connected lighting into measurable building performance."
        artwork={<div className="bg-white border border-zinc-200 p-6"><SensorCone className="w-full h-auto" /></div>}
      />
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 space-y-6">
        {SOLUTIONS.map((s, i) => (
          <Reveal key={s.id}>
            <div
              id={s.id}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-10 bg-white border border-zinc-200 p-8 md:p-12 ${i % 2 === 1 ? "" : ""}`}
              data-testid={`solution-${s.id}`}
            >
              <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                <div className="flex items-center gap-3">
                  {(() => { const SolIcon = SOLUTION_ICONS[s.id]; return SolIcon ? <SolIcon size={26} className="text-signal" strokeWidth={1.6} /> : null; })()}
                  <span className="font-mono text-sm text-signal">/{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-ink mt-3">{s.title}</h2>
                <p className="text-zinc-600 mt-4 leading-relaxed">{s.desc}</p>
                <Link
                  to="/contact?type=demo"
                  onClick={() => track("cta_click", { cta: "talk_specialist", location: `solutions:${s.id}` })}
                  className="inline-flex items-center gap-2 mt-7 text-sm font-medium text-signal hover:gap-3 transition-all"
                  data-testid={`solution-cta-${s.id}`}
                >
                  Talk to an Amaya Specialist <ArrowRight size={15} />
                </Link>
              </div>
              <ul className="space-y-3 self-center">
                {s.points.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-zinc-700 bg-bone border border-zinc-100 px-4 py-3">
                    <Check size={15} className="text-signal shrink-0 mt-0.5" /> {p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </section>
      <CTASection title="Talk to an Amaya specialist." sub="Bring us your floor plan — we'll map the control strategy." />
    </div>
  );
}
